<script setup lang="ts">
import { computed, ref } from 'vue';
import type { StudyModel } from '@/models/study';
import StudySelectorContent from './StudySelectorContent.vue';
import { Popup, StudyAvatar } from '@/components';
import { useRouter } from 'vue-router';
import { LocalStorageUtils, DATA_VIEW_FILTER, routeConstants } from '@/utils';
import { useStudyStore } from '@/stores/study';
import { usePipelineStore } from '@/stores/pipeline';
import { useDashboardStore } from '@/stores/dashboard';

const studyStore = useStudyStore();
const pipelineStore = usePipelineStore();
const dashboardStore = useDashboardStore();

const selectedStudy = computed(() => studyStore.studies?.find(study => study.identifier === studyStore.selectedStudyId));
const visible = ref(false);
const selectorRef = ref<HTMLElement | null>(null);

const xValue = ref('0px');
const yValue = ref('0px');
const maxHeight = ref('90vh');

// Gap between the trigger and the popup, plus the breathing room we keep against the viewport edge.
const POPUP_OFFSET = 6;
const VIEWPORT_MARGIN = 16;

const toggle = () => {
  visible.value = !visible.value;
  const bounds = selectorRef.value?.getBoundingClientRect();
  if (bounds) {
    if (dashboardStore.sidebarCollapsed) {
      // Collapsed rail is too narrow for a dropdown, so the list flies out to its right instead.
      xValue.value = `${bounds.right + POPUP_OFFSET}px`;
      yValue.value = `${bounds.y}px`;
    } else {
      xValue.value = `${bounds.x}px`;
      yValue.value = `${bounds.bottom + POPUP_OFFSET}px`;
    }
    const top = dashboardStore.sidebarCollapsed ? bounds.y : bounds.bottom + POPUP_OFFSET;
    maxHeight.value = `${Math.max(120, window.innerHeight - top - VIEWPORT_MARGIN)}px`;
  }
};

// role="button" on a div doesn't get Enter/Space activation the way a real button
// does, so the trigger has to handle them itself.
const onTriggerKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    toggle();
  } else if (event.key === 'Escape' && visible.value) {
    toggle();
  }
};

// Closing from inside the popup has to hand focus back, or the keyboard user is
// left on a detached element.
const closeAndRefocus = () => {
  if (visible.value) {
    toggle();
  }
  selectorRef.value?.focus();
};

const router = useRouter();
const setSelected = (item: StudyModel) => {
  if (item.longitudinal === true) {
    if (item?.flags?.preferences?.dataViewFilter) {
      LocalStorageUtils.setDataViewFilter(item.identifier, item?.flags?.preferences?.dataViewFilter);
    } else {
      LocalStorageUtils.setDataViewFilter(item.identifier, DATA_VIEW_FILTER.CASES);
    }
  } else {
    LocalStorageUtils.setDataViewFilter(item.identifier, DATA_VIEW_FILTER.ENTRIES);
  }
  studyStore.setStudy(item);
  pipelineStore.resetStore();
  
  visible.value = !visible.value;
  // Focus would otherwise fall to <body> when the popup unmounts.
  selectorRef.value?.focus();
  router.replace({
    name: routeConstants.DATA.name,
    params: { studyIdentifier: item.identifier }
  });
};

</script>

<template>
  <div class="study-selector"
    :class="{ collapsed: dashboardStore.sidebarCollapsed }"
    @click="toggle()">
    <div class="study-selector-header"
      ref="selectorRef"
      :class="{ open: visible }"
      role="button"
      tabindex="0"
      aria-haspopup="listbox"
      :aria-expanded="visible"
      :aria-label="`Study: ${selectedStudy?.title ?? ''}`"
      @keydown="onTriggerKeydown">
      <StudyAvatar v-if="dashboardStore.sidebarCollapsed"
        :avatar="selectedStudy?.avatar"
        :title="selectedStudy?.title"
        v-tooltip.right="selectedStudy?.title"/>
      <template v-else>
        <StudyAvatar :avatar="selectedStudy?.avatar" :title="selectedStudy?.title" />
        <div class="study-label">
          <div class="study-selector-label">{{ selectedStudy?.title }}</div>
          <div class="part11badge" v-if="selectedStudy?.flags?.cfrPart11">21 CFR Part 11 Active</div>
        </div>
        <FontAwesomeIcon :icon="['fas', 'caret-down']" class="caret-down" />
      </template>
    </div>

    <Popup :visible="visible"
      :x="xValue"
      :y="yValue"
      :maxHeight="maxHeight"
      @outsidePopupClick="toggle">
      <template #popupContent>
        <StudySelectorContent :studies="studyStore.studies"
          :selectedStudyId="studyStore.selectedStudyId"
          @studyClick="setSelected"
          @close="closeAndRefocus"/>
      </template>
    </Popup>
  </div>
</template>

<style scoped lang="scss">
@use '../../assets/variables.module' as *;
.study-selector {
  padding: 8px;
  &:hover {
    cursor: pointer;
  }
}
.study-selector.collapsed {
  // Same 8px inset as the collapsed nav pills so both line up in the rail.
  padding: 8px;
  .study-selector-header {
    justify-content: center;
    padding: 4px;
  }
}
.caret-down {
  font-size: 14px;
  color: hsla(0, 0%, 0%, 0.54);
  transition: transform 0.15s ease;
}
.study-selector-header {
  box-sizing: border-box;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background-color: hsl(210, 20%, 96%);
    border-color: hsl(206, 25%, 92%);
  }

  &:focus-visible {
    outline: 2px solid $primary-color;
    outline-offset: -2px;
  }

  &.open {
    background-color: hsl(210, 20%, 96%);
    border-color: hsl(206, 25%, 88%);

    .caret-down {
      transform: rotate(180deg);
    }
  }
}
.study-label {
  // Lets the title ellipsis instead of pushing the caret out of the sidebar.
  flex: 1;
  min-width: 0;
}
.study-selector-label {
  background-color: transparent;
  border: none;
  padding-top: 4px;
  padding-bottom: 4px;
  font-size: 16px;
  font-weight: 400;
  max-width: 100%;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
.part11badge {
  display: inline-block;
  padding: 0.3em 0.7em;
  border-radius: 0.25em;
  font-family: 'Segoe UI', Arial, sans-serif;
  font-size: 0.85em;
  font-weight: bold;
  text-transform: uppercase;
  color: #ffffff;
  background-color: #28a745;
  text-align: center;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
